import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { useInvitedUsers, useUser } from 'app/network/user';
import type { NavigationScreen } from 'app/types/navigation';
import { useScopedTranslation } from 'app/hooks/i18n';
import { Share } from 'react-native';
import { useEffect } from 'react';
import {
  useReferralPrize202506Available,
  useReferralPrizeAvailable,
  useWonReferralPrize,
  useWonReferralPrize202506,
} from 'app/network/promotions';
import Toast from 'react-native-root-toast';
import { RootSiblingParent } from 'react-native-root-siblings';
import * as Clipboard from 'expo-clipboard';
import { Event, track } from 'app/lib/analytics';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { PromoPagePlaceholder } from 'app/components/promo-page-placeholder';
import { getReferralProgramState } from './invite/shared';
import { InviteHeaderImage } from './invite/invite-header-image';
import { InviteCloseButton } from './invite/invite-close-button';
import { InviteIntroduction } from './invite/invite-introduction';
import { ReferralCard } from './invite/referral-card';
import { HowItWorksCard } from './invite/how-it-works-card';
import { InviteDetails2 } from './invite/invite-details2';
import { InviteCodeCard } from './invite/invite-code-card';
import { InvitePageActions } from './invite/invite-page-actions';

const InviteScreenComponent: NavigationScreen<'invite'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const { isLargeUI } = useUISize();
  const { data: user } = useUser({ throwOnError: true });
  const { data: invitedUsers } = useInvitedUsers();

  const referralPrizeAvailable = useReferralPrizeAvailable();
  const referralPrize202506Available = useReferralPrize202506Available();
  const userWonReferralPrize = useWonReferralPrize();
  const userWonReferralPrize202506 = useWonReferralPrize202506();

  const setPageTitle = usePageTitle('invite');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const referralProgram = getReferralProgramState({
    referralPrize202506Available,
    referralPrizeAvailable,
    userWonReferralPrize,
    userWonReferralPrize202506,
  });

  const inviteCode = user?.referralURL.split('/').pop()?.toUpperCase();

  const onCopy = async () => {
    await Clipboard.setStringAsync(inviteCode!);
    Toast.show(t('codeCopied'), {
      duration: Toast.durations.SHORT,
    });
  };

  const shareReferralLink = async () => {
    // Of note: content.url is iOS only; ref: https://reactnative.dev/docs/share
    await Share.share({
      message: t(`${referralProgram.tPrefix}shareText`, {
        ns: 'invite',
        url: user?.referralURL,
      }),
      url: user?.referralURL,
    });
    track(Event.SharedInviteCode);
  };

  const defaultPadding = usePagePadding();
  const pagePadding = { ...defaultPadding, paddingLeft: 0, paddingRight: 0 };

  const showPlaceholder = !user || !invitedUsers || userWonReferralPrize === undefined;
  if (showPlaceholder) {
    return <PromoPagePlaceholder />;
  }
  const numInvited = invitedUsers.length;

  // <RootSiblingParent> is needed here for toast to appear above this screen; since it is a modal, the <RootSiblingParent> in App.tsx makes the toast appear below the modal. Adding it here ensures it appears above it.
  return (
    <RootSiblingParent>
      <Page
        accessory={<InvitePageActions onCopy={() => void onCopy()} onShare={() => void shareReferralLink()} />}
        paddingStyle={pagePadding}
      >
        <InviteHeaderImage />
        <InviteCloseButton isLargeUI={isLargeUI} onClose={() => navigation.goBack()} />

        <Column gap="spacing.general.x4" marginTop="spacing.general.x4" paddingHorizontal="spacing.general.x4">
          <InviteIntroduction
            numInvited={numInvited}
            tPrefix={referralProgram.tPrefix}
            tSuffix={referralProgram.tSuffix}
          />

          <ReferralCard
            invitedUsers={invitedUsers}
            isReferralProgram={referralProgram.isReferralProgram}
            isReferralProgram202506={referralProgram.isReferralProgram202506}
            numInvited={numInvited}
            tPrefix={referralProgram.tPrefix}
          />
          <InviteDetails2 numInvited={numInvited} tPrefix={referralProgram.tPrefix} tSuffix={referralProgram.tSuffix} />
          <HowItWorksCard
            isReferralProgram={referralProgram.isReferralProgram}
            isReferralProgram202506={referralProgram.isReferralProgram202506}
            tPrefix={referralProgram.tPrefix}
          />
          <InviteCodeCard inviteCode={inviteCode} onCopy={() => void onCopy()} />
        </Column>
      </Page>
    </RootSiblingParent>
  );
};

export const InviteScreen: NavigationScreen<'invite'> = withErrorBoundary(InviteScreenComponent);
